/**
 * Concert-pitch helpers for Performance-mode parts.
 *
 * Rules from Bowie (spec `docs/ux/2026-10-12-performance-roles-and-notes-ux.md`), pinned by Glenn's
 * `concert-pitch.test.ts`:
 * - concert = guitar shape + capo semitones, spelled by the CONCERT key;
 * - a key is spelled with its own scale, so minor keys use the minor scale with the raised 7th
 *   (G minor: D#dim → F#dim), not the relative major;
 * - only the root and a slash bass are transposed; the chord quality is kept verbatim.
 *
 * Output is ASCII (`Bb`, `F#`), like `NoteName`. Glyphs are a display concern (`displayAccidentals`).
 * Does not use `NoteLib.transpose` (it ignores the key) or `chord-regexp.ts` (a fixed type list).
 */

import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import type { Chord, ChordNotes } from './chord-types.js'
import { isChordNotes, isChordVariant } from './chord-types.js'

const letters = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
type Letter = (typeof letters)[number]
const naturalPitch: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

const sharpNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const
const flatNames = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const

/**
 * The conventional name of a key on each pitch class (the fewest accidentals). Exported so a
 * correction from Bowie is a data edit. Theoretical keys (Gb vs F#, Cb) are follow-up F8.
 */
export const concertKeyNames = {
  major: ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'],
  minor: ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'],
} as const satisfies Record<'major' | 'minor', readonly NoteName[]>

const modeIntervals: Record<ScaleType, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  ionian: [0, 2, 4, 5, 7, 9, 11],
  majorpentatonic: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  minorpentatonic: [0, 2, 3, 5, 7, 8, 10],
  harmonicminor: [0, 2, 3, 5, 7, 8, 10],
  melodicminor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
}

/** Minor keys whose leading tone is spelled as a raised 7th (G minor: F#, not Gb). */
const raisedSeventh = new Set<ScaleType>(['minor', 'aeolian', 'minorpentatonic', 'harmonicminor', 'melodicminor'])

const isMinor = (type?: ScaleType) => Boolean(type && raisedSeventh.has(type))

/** ♯/♭ glyphs to ASCII. */
const toAscii = (text: string) => text.replace(/♯/g, '#').replace(/♭/g, 'b')

const mod12 = (n: number) => ((n % 12) + 12) % 12

const notePattern = /^([A-G])([#b]?)$/

/** Pitch class of an ASCII note name, or undefined. */
const pitchClassOf = (note: string): number | undefined => {
  const match = notePattern.exec(note)
  if (!match) {
    return undefined
  }
  const accidental = match[2] === '#' ? 1 : match[2] === 'b' ? -1 : 0
  return mod12(naturalPitch[match[1] as Letter] + accidental)
}

const accidentalFor = (offset: number) => (offset === 1 ? '#' : offset === -1 ? 'b' : offset === 2 ? '##' : 'bb')

/**
 * The key's own spelling of each pitch class it contains, plus the raised 7th in minor keys unless
 * `signatureOnly` (the key signature: what its accidental count is read from).
 */
const keySpellings = (key: Scale, signatureOnly = false): Map<number, string> => {
  const rootPitch = pitchClassOf(key.root)
  const spellings = new Map<number, string>()
  if (rootPitch === undefined) {
    return spellings
  }
  const rootLetter = letters.indexOf(key.root[0] as Letter)
  const intervals = modeIntervals[key.type ?? 'major']
  intervals.forEach((interval, degree) => {
    const letter = letters[(rootLetter + degree) % 7]
    const pitch = mod12(rootPitch + interval)
    const offset = ((pitch - naturalPitch[letter] + 18) % 12) - 6
    spellings.set(pitch, offset === 0 ? letter : `${letter}${accidentalFor(offset)}`)
  })
  if (isMinor(key.type) && !signatureOnly) {
    const letter = letters[(rootLetter + 6) % 7]
    const pitch = mod12(rootPitch + 11)
    const offset = ((pitch - naturalPitch[letter] + 18) % 12) - 6
    spellings.set(pitch, offset === 0 ? letter : `${letter}${accidentalFor(offset)}`)
  }
  return spellings
}

/** Whether a key's signature is written with flats (so chromatic notes take flats too). */
const usesFlats = (key: Scale) =>
  [...keySpellings({ root: key.root, type: isMinor(key.type) ? 'minor' : key.type }).values()].some(
    name => name.length > 1 && name.endsWith('b')
  )

/**
 * Spell a pitch class (0–11) in a key: by the key's own scale when the note belongs to it, otherwise
 * with the key's accidental direction (chromatic spelling beyond that is follow-up F5).
 */
export const spellInKey = (pitchClass: number, key: Scale): NoteName => {
  const pitch = mod12(pitchClass)
  const inKey = keySpellings(key).get(pitch)
  if (inKey && notePattern.test(inKey)) {
    return inKey as NoteName
  }
  return (usesFlats(key) ? flatNames : sharpNames)[pitch]
}

/**
 * Whether a note belongs to a key (its scale, plus the raised 7th in minor keys). Used to judge which
 * key a chart's chords fit.
 */
export const isInKey = (note: string, key: Scale) => {
  const pitch = pitchClassOf(toAscii(note.trim()))
  return pitch !== undefined && keySpellings(key).has(pitch)
}

/** The key a chart sounds in: written key + capo for guitar shapes; a concert chart is already there. */
export const concertKeyOf = (
  written: Scale | undefined,
  capo: number,
  writtenAs: 'shapes' | 'concert' = 'shapes'
): Scale | undefined => {
  if (!written) {
    return undefined
  }
  const rootPitch = pitchClassOf(toAscii(written.root))
  if (writtenAs === 'concert' || capo === 0 || rootPitch === undefined) {
    return written
  }
  const names = isMinor(written.type) ? concertKeyNames.minor : concertKeyNames.major
  const root = names[mod12(rootPitch + capo)]
  return written.type ? { root, type: written.type } : { root }
}

const rootPattern = /^([A-G][#b]?)(.*)$/

/** Transpose one ASCII note name into the concert key, or undefined when it is not a note name. */
const transposeNote = (note: string, semitones: number, key: Scale) => {
  const pitch = pitchClassOf(note)
  return pitch === undefined ? undefined : spellInKey(pitch + semitones, key)
}

/**
 * The concert-pitch name of a chord written as a guitar shape under a capo. The root and a slash bass
 * move up `capo` semitones and are spelled by the concert `key` (a `Scale` with no `type` is major).
 * The quality is kept verbatim. Capo 0, and anything that is not a chord (N.C., ''), is returned as is.
 */
export const toConcert = (chord: string, capo: number, key: Scale): string => {
  if (capo === 0) {
    return chord
  }
  const ascii = toAscii(chord.trim())
  const match = rootPattern.exec(ascii)
  if (!match) {
    return chord
  }
  const [, root, rest] = match
  const slash = rest.lastIndexOf('/')
  const bass = slash >= 0 ? rest.slice(slash + 1) : undefined
  const bassIsNote = bass !== undefined && notePattern.test(bass)
  const quality = bassIsNote ? rest.slice(0, slash) : rest
  const concertRoot = transposeNote(root, capo, key) ?? root
  const concertBass = bassIsNote ? transposeNote(bass, capo, key) : undefined
  return concertBass ? `${concertRoot}${quality}/${concertBass}` : `${concertRoot}${quality}`
}

const noChord = new Set(['N.C.', '-', 'x'])

/** Whether a chord symbol means "no chord" (N.C., - or x). Other N.C. spellings are follow-up F6. */
export const isNoChord = (chord: string) => noChord.has(chord.trim())

/** The major key on a note's pitch class: how a chord with no song key is spelled. */
const ownKey = (note: string, semitones: number): Scale | undefined => {
  const pitch = pitchClassOf(toAscii(note))
  return pitch === undefined ? undefined : { root: concertKeyNames.major[mod12(pitch + semitones)] }
}

/** One note name moved and spelled in `key`; anything that is not a note name is kept. */
const moveNote = (note: string, semitones: number, key: Scale) => transposeNote(toAscii(note), semitones, key) ?? note

const moveNotes = (chord: ChordNotes, semitones: number, key: Scale | undefined): ChordNotes => {
  const spelling = key ?? ownKey(chord.name.match(rootPattern)?.[1] ?? '', semitones)
  if (!spelling) {
    return chord
  }
  return {
    ...chord,
    name: toConcert(chord.name, semitones, spelling),
    notes: chord.notes.map(note => moveNote(note, semitones, spelling) as NoteName),
  }
}

/**
 * A chord element moved `semitones` (a capo, minus a capo for shapes on a concert chart, plus the
 * person's transpose), with its root and slash bass spelled in `key`: the key the result sounds in.
 * Without a key, the root and bass are spelled in the major key of the new root. The quality is kept
 * verbatim. A no-chord, and a move of 0 (mod 12), return the same chord.
 */
export const transposeChordInKey = <T extends Chord>(chord: T, semitones: number, key: Scale | undefined): T => {
  if (mod12(semitones) === 0) {
    return chord
  }
  if (isChordVariant(chord)) {
    const spelling = key ?? ownKey(chord.root, semitones)
    if (!spelling) {
      return chord
    }
    const root = moveNote(chord.root, semitones, spelling) as NoteName
    const bassNote = chord.bassNote ? (moveNote(chord.bassNote, semitones, spelling) as NoteName) : undefined
    return bassNote ? { ...chord, root, bassNote } : { ...chord, root }
  }
  if (isChordNotes(chord) && !isNoChord(chord.name)) {
    return moveNotes(chord, semitones, key) as T
  }
  return chord
}

/** The root and slash bass of a chord element, as ASCII note names; none for a no-chord. */
const chordNotesOf = (chord: Chord): string[] => {
  if (isChordVariant(chord)) {
    return chord.bassNote ? [chord.root, chord.bassNote] : [chord.root]
  }
  if (!isChordNotes(chord) || isNoChord(chord.name)) {
    return []
  }
  const match = rootPattern.exec(toAscii(chord.name.trim()))
  if (!match) {
    return []
  }
  const slash = match[2].lastIndexOf('/')
  const bass = slash >= 0 ? match[2].slice(slash + 1) : undefined
  return bass && notePattern.test(bass) ? [match[1], bass] : [match[1]]
}

/** Minor-quality chord ('m', 'm7', 'm7b5'), not 'maj7'. */
const isMinorChord = (chord: Chord) => {
  const quality = isChordVariant(chord)
    ? chord.variant
    : (rootPattern.exec(toAscii(isChordNotes(chord) ? chord.name.trim() : ''))?.[2] ?? '')
  return /^m(?!aj)/.test(quality)
}

/** Whether the chord is the key's tonic chord: its root on the tonic, minor for a minor key. */
const isTonicChord = (chord: Chord | undefined, key: Scale) => {
  const root = chord ? chordNotesOf(chord)[0] : undefined
  return (
    root !== undefined &&
    chord !== undefined &&
    pitchClassOf(root) === pitchClassOf(key.root) &&
    isMinorChord(chord) === isMinor(key.type)
  )
}

/** The quality text after the root ('m7', 'sus4', 'dim', '+'), without a slash bass. */
const qualityOf = (chord: Chord) => {
  const text = isChordVariant(chord)
    ? chord.variant
    : (rootPattern.exec(toAscii(isChordNotes(chord) ? chord.name.trim() : ''))?.[2] ?? '')
  const slash = text.lastIndexOf('/')
  return slash >= 0 ? text.slice(0, slash) : text
}

/**
 * The triad of a chord quality as semitones above the root (Bowie): sus and power chords sound only the
 * root and fifth; diminished (and half-diminished) a minor third and diminished fifth; augmented a major
 * third and augmented fifth; minor a minor third; anything else a major triad.
 */
const triadOf = (quality: string): readonly number[] => {
  if (quality.includes('sus') || quality === '5') {
    return [0, 7]
  }
  if (/^(dim|°|ø|m7b5)/.test(quality)) {
    return [0, 3, 6]
  }
  if (/^(aug|\+)/.test(quality)) {
    return [0, 4, 8]
  }
  if (/^m(?!aj)/.test(quality)) {
    return [0, 3, 7]
  }
  return [0, 4, 7]
}

/** The pitch classes a chord adds to a key's score: its triad tones, plus a slash bass. */
const scoredPitchesOf = (chord: Chord): number[] => {
  const [root, bass] = chordNotesOf(chord)
  const rootPitch = root === undefined ? undefined : pitchClassOf(root)
  if (rootPitch === undefined) {
    return []
  }
  const bassPitch = bass === undefined ? undefined : pitchClassOf(bass)
  const triad = triadOf(qualityOf(chord)).map(interval => mod12(rootPitch + interval))
  return bassPitch === undefined ? triad : [...triad, bassPitch]
}

/** Every major and minor key, by its conventional name. */
const candidateKeys: Scale[] = [
  ...concertKeyNames.major.map(root => ({ root })),
  ...concertKeyNames.minor.map(root => ({ root, type: 'minor' as const })),
]

const accidentalCount = (key: Scale) =>
  [...keySpellings(key, true).values()].reduce((count, name) => count + name.length - 1, 0)

/**
 * The key a song without one is in, inferred from its chords (Bowie): every major and minor key scores
 * one point per chord tone in its scale (each chord's triad by quality, plus a slash bass). Ties go to a key whose tonic chord opens or
 * closes the song (root on the tonic, and minor for a minor key: Em…D is E minor, not D minor), then to
 * the fewest accidentals in the key signature. Fewer than two chords (no-chords aside) infer nothing.
 */
export const inferKey = (chords: readonly Chord[]): Scale | undefined => rankKeys(chords)[0]?.key

interface RankedKey {
  key: Scale
  /** Chord tones (triads plus slash basses) that belong to the key. */
  score: number
  /** 1 when the first or last chord is the key's tonic chord. */
  tonicAtEnds: 0 | 1
  accidentals: number
}

/** The sounding chords: no-chords and unparseable symbols carry no evidence. */
const soundingChords = (chords: readonly Chord[]) => chords.filter(chord => chordNotesOf(chord).length > 0)

/** Every candidate key ranked by `inferKey`'s rule, best first. Empty below two sounding chords. */
const rankKeys = (chords: readonly Chord[]): RankedKey[] => {
  const sounding = soundingChords(chords)
  if (sounding.length < 2) {
    return []
  }
  const pitches = sounding.flatMap(scoredPitchesOf)
  const ends = [sounding[0], sounding[sounding.length - 1]]
  const rank = (key: Scale): RankedKey => {
    const spellings = keySpellings(key)
    return {
      key,
      score: pitches.filter(pitch => spellings.has(pitch)).length,
      tonicAtEnds: ends.some(chord => isTonicChord(chord, key)) ? 1 : 0,
      accidentals: accidentalCount(key),
    }
  }
  return candidateKeys
    .map(rank)
    .sort((a, b) => b.score - a.score || b.tonicAtEnds - a.tonicAtEnds || a.accidentals - b.accidentals)
}

/**
 * Every key the evidence can't separate, for analysis that should hold in all of them (the cover-song
 * chord check, Bowie Q4 on #2364). `decided` is `inferKeyToStore`'s answer; when it is undefined,
 * `candidates` are the keys tied with the top on chord-tone score and on a tonic chord at the ends.
 * `fit` is the share of the chart's chord tones that belong to `decided` (0 when nothing is decided).
 */
export const inferKeyCandidates = (
  chords: readonly Chord[]
): { decided: Scale | undefined; candidates: Scale[]; fit: number } => {
  const ranked = rankKeys(chords)
  const [best] = ranked
  if (!best) {
    return { decided: undefined, candidates: [], fit: 0 }
  }
  const decided = inferKeyToStore(chords)
  const candidates = ranked
    .filter(key => key.score === best.score && key.tonicAtEnds === best.tonicAtEnds)
    .map(({ key }) => key)
  const pitches = soundingChords(chords).flatMap(scoredPitchesOf)
  const spellings = decided ? keySpellings(decided) : undefined
  const fit =
    spellings && pitches.length > 0 ? pitches.filter(pitch => spellings.has(pitch)).length / pitches.length : 0
  return { decided, candidates, fit }
}

/** Distinct chord-root pitch classes: `D`, `Dsus4` and `D5` are one root. */
const distinctRoots = (chords: readonly Chord[]) =>
  new Set(
    chords.flatMap(chord => {
      const root = chordNotesOf(chord)[0]
      const pitch = root === undefined ? undefined : pitchClassOf(root)
      return pitch === undefined ? [] : [pitch]
    })
  ).size

/** Below this many distinct roots there is too little evidence to store a key (Bowie e). */
const minDistinctRootsToStore = 3

/** A chord that opens and closes the song needs only this many distinct roots (Bowie, #2377). */
const minDistinctRootsForBookends = 2

/** Same root pitch class and the same quality text, slash bass aside. */
const isSameChord = (a: Chord, b: Chord) => {
  const [rootA] = chordNotesOf(a)
  const [rootB] = chordNotesOf(b)
  return (
    rootA !== undefined &&
    rootB !== undefined &&
    pitchClassOf(rootA) === pitchClassOf(rootB) &&
    qualityOf(a) === qualityOf(b)
  )
}

/**
 * The key a chord names as a tonic, from its triad: major or minor only. Sus, power, diminished and
 * augmented chords give no quality, so they name no key (Bowie d).
 */
const keyNamedBy = (chord: Chord): Scale | undefined => {
  const [root] = chordNotesOf(chord)
  const triad = triadOf(qualityOf(chord))
  if (root === undefined || triad.length !== 3 || triad[2] !== 7) {
    return undefined
  }
  return triad[1] === 3 ? { root: root as NoteName, type: 'minor' } : { root: root as NoteName }
}

/** Whether every tone of the chord's triad belongs to the key. */
const isDiatonicTo = (chord: Chord, key: Scale) => {
  const [root] = chordNotesOf(chord)
  const rootPitch = root === undefined ? undefined : pitchClassOf(root)
  if (rootPitch === undefined) {
    return false
  }
  const spellings = keySpellings(key)
  return triadOf(qualityOf(chord)).every(interval => spellings.has(mod12(rootPitch + interval)))
}

/**
 * The key to STORE for a song that states none (Bowie, 2026-10-18, PR #2364).
 *
 * `inferKey` always answers, which is right for spelling chords on screen: a guess there costs
 * nothing. A stored key shows in the song header as a fact, so this answers only when the evidence
 * decides it, and otherwise returns nothing:
 *
 * - **Too little evidence:** fewer than three distinct chord roots.
 * - **The same chord opens and closes the song** but is not the winning key's tonic, while being
 *   diatonic to it: that chord is the tonic, in its own triad quality. D C G D is D major (the C is
 *   Mixolydian ♭VII), Am D Am is A minor (Dorian), Em F Em is E minor (Phrygian). A mode type is never
 *   inferred; it waits until a chart states one. A sus, power or diminished chord there names no
 *   quality, so nothing is stored.
 * - **Otherwise**, the `inferKey` winner is stored only when it beat the runner-up on chord-tone score,
 *   or tied on score and won on a tonic chord at the start or end. The score can never separate
 *   relative keys (they share a pitch set), and when the ends don't decide either, the remaining
 *   tiebreaks are accidentals and list order: a coin flip, so nothing is stored. That includes one
 *   relative tonic at each end (C … Am) and I–V vamps like C G C G.
 *
 * Whenever this stores a key by the score or the ends, it is `inferKey`'s answer, so the stored key
 * and the key chords are spelled in on screen never diverge.
 */
export const inferKeyToStore = (chords: readonly Chord[]): Scale | undefined => {
  const sounding = soundingChords(chords)
  const [best, runnerUp] = rankKeys(sounding)
  if (!best) {
    return undefined
  }
  const first = sounding[0]
  const last = sounding[sounding.length - 1]
  const roots = distinctRoots(sounding)
  /**
   * A chord that opens and closes the song names its own tonic, so it is checked BEFORE the three-root
   * floor, which guards score-based inference (Bowie, #2377): Am D Am is A minor, and so is Am E Am. It
   * still needs two distinct roots, so a chart of one chord stores nothing.
   *
   * - Not the winner's tonic but diatonic to it: that chord is the tonic (D C G D is D major). A chord
   *   with no quality (sus, power, diminished) names nothing, so nothing is stored.
   * - The winner's own tonic: store it. When the chord names no quality, fall through to the usual
   *   rules rather than storing nothing, so Dsus4 G A Dsus4 is still D major by the ends.
   */
  if (isSameChord(first, last) && roots >= minDistinctRootsForBookends) {
    if (!isTonicChord(first, best.key) && isDiatonicTo(first, best.key)) {
      return keyNamedBy(first)
    }
    const named = isTonicChord(first, best.key) ? keyNamedBy(first) : undefined
    if (named) {
      return named
    }
  }
  if (roots < minDistinctRootsToStore) {
    return undefined
  }
  const isDecided =
    !runnerUp ||
    best.score > runnerUp.score ||
    (best.score === runnerUp.score && best.tonicAtEnds > runnerUp.tonicAtEnds)
  return isDecided ? best.key : undefined
}

const chartNotePattern = /^[A-G][#b♯♭]?$/
const chartRootPattern = /^[A-G][#b♯♭]?/

/**
 * The bass note of a chord symbol: the note after the last slash when it is a note name, otherwise the
 * root (never an inferred inversion). Keeps the chart's spelling, glyphs included. N.C. stays N.C.; an
 * empty token stays empty.
 */
export const bassNote = (chord: string): string => {
  const text = chord.trim()
  if (isNoChord(text)) {
    return 'N.C.'
  }
  const slash = text.lastIndexOf('/')
  const afterSlash = slash >= 0 ? text.slice(slash + 1) : ''
  if (chartNotePattern.test(afterSlash)) {
    return afterSlash
  }
  return chartRootPattern.exec(text)?.[0] ?? ''
}

/** ASCII accidentals to ♯/♭ glyphs, for display only. */
export const displayAccidentals = (text: string) => text.replace(/([A-G])#/g, '$1♯').replace(/([A-G])b/g, '$1♭')

/** A key for display: "B♭", "C♯m". */
export const keyName = (key: Scale) => `${displayAccidentals(key.root)}${isMinor(key.type) ? 'm' : ''}`

const keyPattern = /^([A-G](?:#|b|♯|♭)?)\s*(m|min|minor|maj|major)?$/i

/** Parse a chart's stated key ("Bb", "B♭", "Em", "G minor", "C# min"). Not a key → undefined. */
export const parseKey = (text: string): Scale | undefined => {
  const match = keyPattern.exec(text.trim())
  if (!match) {
    return undefined
  }
  const root = toAscii(match[1].charAt(0).toUpperCase() + match[1].slice(1))
  if (pitchClassOf(root) === undefined) {
    return undefined
  }
  const quality = match[2]?.toLowerCase()
  const minor = quality === 'm' || quality === 'min' || quality === 'minor'
  return minor ? { root: root as NoteName, type: 'minor' } : { root: root as NoteName }
}
